/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import ProductTile, {Skeleton} from '../product-tile'
import {
    bodyStyle,
    containerStyle,
    headingStyle,
    iconButtonLeftHoverStyle,
    iconButtonLeftStyle,
    iconButtonRightHoverStyle,
    iconButtonRightStyle,
    recBoxStyle,
    recBoxStyleForAddToCart,
    scrollContainerStyle,
    skeletonBoxStyle,
    skeletonForAddToCart,
    arrowContainerLeftStyle,
    arrowContainerRightStyle
} from './index.style'
import {RightArrow, RightArrowHover} from '../icons'
import {transformProductRecommendation} from '../../utills/transformProductRecommendation'
import useEinstein from '../../hooks/use-einstein'

/**
 * A component for fetching and rendering product recommendations from the Einstein API
 * by a zone or a recommender name.
 */
const RecommendedProducts = ({
    zone,
    recommender,
    products,
    title,
    shouldFetch,
    isFromAddToCart = false,
    previousSearchParams,
    showQuickView,
    containerCustomStyle,
    ...props
}) => {
    const {
        isLoading,
        recommendations,
        getZoneRecommendations,
        getRecommendations,
        sendClickReco,
        sendViewReco,
        sendViewProduct
    } = useEinstein()

    const [_products, setProducts] = useState(products)
    const ref = useRef()
    const scrollContainerRef = useRef()
    const isOnScreen = useIntersectionObserver(ref, {useOnce: true})
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [newProducts, setNewProducts] = useState([])

    const checkScrollPosition = () => {
        if (scrollContainerRef.current) {
            const {scrollLeft, scrollWidth, clientWidth} = scrollContainerRef.current
            setCanScrollLeft(scrollLeft > 0)
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 3)
        }
    }

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            const scrollAmount = scrollContainerRef.current.clientWidth * 0.25
            scrollContainerRef.current.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            })
        }
    }

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            const scrollAmount = scrollContainerRef.current.clientWidth * 0.25
            scrollContainerRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            })
        }
    }

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        if (scrollContainer) {
            checkScrollPosition()
            scrollContainer.addEventListener('scroll', checkScrollPosition)
            const resizeObserver = new ResizeObserver(checkScrollPosition)
            resizeObserver.observe(scrollContainer)
            return () => {
                scrollContainer.removeEventListener('scroll', checkScrollPosition)
                resizeObserver.disconnect()
            }
        }
    }, [recommendations])

    useEffect(() => {
        // Check if the component should fetch results or not. This is useful
        // when you are still waiting on additional data, like `products`.
        if (typeof shouldFetch === 'function' && !shouldFetch()) {
            return
        }

        // Fetch either zone or recommender, but not both. If a zone and recommender
        // name are both provided, `zone` takes precendence.
        if (zone) {
            getZoneRecommendations(zone, _products)
            return
        }
        if (recommender) {
            sendViewProduct(_products?.[0])
            getRecommendations(recommender, _products)
            return
        }
    }, [zone, recommender, _products])

    useEffect(() => {
        // This is an optimization that eliminates superfluous rerenders/fetching by
        // keeping a copy of the `products` array prop in state for shallow comparison.
        if (!Array.isArray(products)) {
            return
        }
        if (
            products.length !== _products?.length ||
            !products.every((val, index) => val === _products?.[index])
        ) {
            setProducts(products)
        }
    }, [products])

    useEffect(() => {
        if (isOnScreen && recommendations !== undefined && recommendations?.recs !== undefined) {
            sendViewReco(
                {
                    recommenderName: recommendations.recommenderName,
                    __recoUUID: recommendations.recoUUID
                },
                recommendations?.recs.map((rec) => ({id: rec.id}))
            )
        }
    }, [isOnScreen, recommendations])

    useEffect(() => {
        if (recommendations?.recs?.length) {
            try {
                const transformedProducts = recommendations.recs
                    .map((product) => transformProductRecommendation(product))
                    .filter((product) => product != null)
                setNewProducts(transformedProducts)
            } catch (error) {
                setNewProducts([])
            }
        } else {
            setNewProducts([])
        }
    }, [recommendations?.recs])

    if (!isLoading && (!recommendations || recommendations.length < 1)) {
        return null
    }

    return (
        <Box sx={containerCustomStyle || containerStyle}>
            {newProducts?.length > 0 && (
                <Text className="recommendation-title" sx={headingStyle}>
                    {title}
                </Text>
            )}
            <Box sx={bodyStyle}>
                {!isLoading && canScrollLeft && (
                    <Box sx={arrowContainerLeftStyle} onClick={scrollLeft} className="arrowLeft">
                        <RightArrow sx={iconButtonLeftStyle} />
                        <RightArrowHover sx={iconButtonLeftHoverStyle} />
                    </Box>
                )}
                {!isLoading && canScrollRight && (
                    <Box sx={arrowContainerRightStyle} onClick={scrollRight} className="arrowRight">
                        <RightArrow sx={iconButtonRightStyle} />
                        <RightArrowHover sx={iconButtonRightHoverStyle} />
                    </Box>
                )}
                <Flex ref={scrollContainerRef} sx={scrollContainerStyle} className="reco-products">
                    {isLoading
                        ? Array.from({length: newProducts.length})?.map((_, idx) => (
                              <Box
                                  key={idx}
                                  sx={isFromAddToCart ? skeletonForAddToCart : skeletonBoxStyle}
                              >
                                  <Skeleton />
                              </Box>
                          ))
                        : Array.isArray(newProducts) && newProducts.length > 0
                        ? newProducts.map((rec) => (
                              <Box
                                  key={rec?.id}
                                  sx={isFromAddToCart ? recBoxStyleForAddToCart : recBoxStyle}
                              >
                                  <ProductTile
                                      product={rec}
                                      previousSearchParams={previousSearchParams}
                                      showQuickView={showQuickView}
                                      onClick={() =>
                                          sendClickReco(
                                              {
                                                  recommenderName: recommendations.recommenderName,
                                                  __recoUUID: recommendations.recoUUID
                                              },
                                              rec
                                          )
                                      }
                                  />
                              </Box>
                          ))
                        : null}
                </Flex>
            </Box>
        </Box>
    )
}

RecommendedProducts.propTypes = {
    title: PropTypes.any,
    zone: PropTypes.string,
    recommender: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.object),
    shouldFetch: PropTypes.func,
    isFromAddToCart: PropTypes.func,
    previousSearchParams: PropTypes.string,
    showQuickView: PropTypes.bool,
    containerCustomStyle: PropTypes.string
}

export default RecommendedProducts
